import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Plus, Edit, Trash2, X, AlertCircle, Loader } from 'lucide-react';
import { useToast } from "../components/ui/toast-provider";

// Servicios
import { EmpleadoService } from "../services/empleadoService";
import { AgenteService } from "../services/agenteService";
import { MunicipioService } from "../services/municipioService";

// Tipos de Response
import { EmpleadoResponse} from "../types/empleado";
import { MunicipioResponse } from "../types/puesto";

export default function CrearAgente({ isOpen, onGuardado, onClose, idEditando }: { isOpen: boolean; onGuardado: () => void; onClose: () => void; idEditando: number }) {

    const [isLoading, setIsLoading] = useState(true);
    const [empleados, setEmpleados] = useState<EmpleadoResponse[]>([]);
    const [municipios, setMunicipios] = useState<MunicipioResponse[]>([]);
    const [error, setError] = useState("");
    const [editando, setEditando] = useState(false);

    const { addToast } = useToast();

    const [formulario, setFormulario] = useState({
        usuario: '',
        agente: '',
        nombreNegocio: '',
        propietario: '',
        ubicacion: '',
        idEmpleado: '',
        activo: true,
        noPoliza: 'DV-',
        monto: '',
        idMunicipio: ''
    });

    const resetFormulario = () => {
        setFormulario({
        usuario: '',
        agente: '',
        nombreNegocio: '',
        propietario: '',
        ubicacion: '',
        idEmpleado: '',
        activo: true,
        noPoliza: 'DV-',
        monto: '',
        idMunicipio: ''
        });
    };

    const cargarEmpleados = async (id: number) => {
        try {
            setIsLoading(true);
            const response = await EmpleadoService.getSinAgente(id);
            setEmpleados(response.data);
        } catch (err) {
            console.error("Error al cargar empleados: " + err);
        } finally {
            setIsLoading(false);
        }
    }

    const cargarMunicipios = async (id: number) => {
        try {
          setIsLoading(true);
          if(id!=0){
            const res = await MunicipioService.getConUsado(id);
            setMunicipios(res.data);
          }else{
            const res = await MunicipioService.getAll(true);
            setMunicipios(res.data);
          }
        } catch (err) {
          setError("Error al cargar Municipios");
        } finally {
          setIsLoading(false);
        }
    };

    const cargarAgente = async (id: number) => {
        try {
            setIsLoading(true);
            const response = await AgenteService.getById(id);
            const agente = response.data;
            setFormulario({
                usuario: agente.usuario,
                agente: agente.agente,
                nombreNegocio: agente.nombreNegocio,
                propietario: agente.propietario,
                ubicacion: agente.ubicacion,
                idEmpleado: agente.idEmpleado,
                activo: agente.activo,
                noPoliza: agente.noPoliza,
                monto: agente.monto,
                idMunicipio: agente.idMunicipio
            });

            const idMunicipio = agente.idMunicipio;
            await cargarMunicipios(idMunicipio);
        } catch (err) {
            console.error("Error al cargar agente: " + err);
        } finally {
            setIsLoading(false);
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            if (editando) {
                await AgenteService.actualizar(idEditando, {
                    ...formulario,
                    idEmpleado: Number(formulario.idEmpleado),
                    idMunicipio: Number(formulario.idMunicipio),
                    monto: Number(formulario.monto)
                });
            } else {
                await AgenteService.crear({
                    ...formulario,
                    idEmpleado: Number(formulario.idEmpleado),
                    idMunicipio: Number(formulario.idMunicipio),
                    monto: Number(formulario.monto),
                    activo: true
                });
            }

            addToast(editando ? "Agente actualizado exitosamente" : "Agente creado exitosamente");
            resetFormulario();
            onGuardado();
        } catch (err:any) {
              const message =
                err?.response?.data?.message ||
                "Error inesperado al guardar agente";

            setError(message);
        }
    }

    useEffect(() => {
        if(isOpen) {
            resetFormulario();
            if (idEditando !== 0) {
                cargarAgente(idEditando);
            }else{
                cargarMunicipios(0);
            }
            setEditando(idEditando !== 0);
            cargarEmpleados(idEditando);
            setError("");
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }
    return (
        <div className="fixed inset-0 bg-opacity-50 flex items-center justify-center z-50">
            <div className="rounded-2xl w-full max-w-2xl shadow-lg relative max-h-[90vh] overflow-y-auto scrollbar-thin">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>{editando ? 'Editar Agente' : 'Crear Agente'}</CardTitle>
                        <button
                        onClick={onClose}
                        className="p-2 hover:bg-destructive/10 rounded-lg transition-colors"
                        >
                        <X className="w-5 h-5 text-destructive" />
                        </button>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input
                            label="Usuario"
                            type="text"
                            value={formulario.usuario}
                            onChange={(e) =>
                                setFormulario({ ...formulario, usuario: e.target.value })
                            }
                            placeholder="Ingrese el usuario"
                            required
                            />
                            <Input
                            label="Agente"
                            type="text"
                            value={formulario.agente}
                            onChange={(e) =>
                                setFormulario({ ...formulario, agente: e.target.value })
                            }
                            placeholder="Ingrese el agente"
                            required
                            />
                            <Input
                            label="Nombre del Negocio"
                            type="text"
                            value={formulario.nombreNegocio}
                            onChange={(e) =>
                                setFormulario({ ...formulario, nombreNegocio: e.target.value })
                            }
                            placeholder="Ingrese el nombre del negocio"
                            required
                            />
                            <Input
                            label="Nombres y Apellidos"
                            type="text"
                            value={formulario.propietario}
                            onChange={(e) =>
                                setFormulario({ ...formulario, propietario: e.target.value })
                            }
                            placeholder="Ingrese el nombre del propietario"
                            required
                            />
                            <Select
                            label="Operador"
                            value={formulario.idEmpleado}
                            onChange={(e) =>
                                setFormulario({ ...formulario, idEmpleado: e.target.value })
                            }
                            options={empleados.map((e) => ({ value: (e.idEmpleado!).toString(), label: e.nombreCompleto || `${e.nombres} ${e.apellidos}` }))}
                            required
                            />
                            <Input
                            label="Ubicación"
                            type="text"
                            value={formulario.ubicacion}
                            onChange={(e) =>
                                setFormulario({ ...formulario, ubicacion: e.target.value })
                            }
                            placeholder="Ingrese la ubicación"
                            required
                            />
                            <Input
                            label="No de Póliza"
                            type="text"
                            value={formulario.noPoliza}
                            onChange={(e) =>{
                                const numeros = e.target.value.replace(/\D/g, "");
                                setFormulario({ ...formulario, noPoliza: `DV-${numeros}` });
                            }}
                            placeholder="DV-Ingrese el No de Póliza"
                            required
                            />
                            <Input
                            label="Monto"
                            type="number"
                            min="0"
                            step="0.01"
                            value={formulario.monto}
                            onChange={(e) =>
                                setFormulario({ ...formulario, monto: e.target.value })
                            }
                            placeholder="0"
                            required
                            />
                            <Select
                            label="Municipio"
                            value={formulario.idMunicipio}
                            onChange={(e) =>
                                setFormulario({ ...formulario, idMunicipio: e.target.value })
                            }
                            options={municipios.map((e) => ({ value: (e.idMunicipio!).toString(), label: e.municipio || `` }))}
                            required
                            />
                        </div>
                        <div className = "flex justify-center">
                        {error && (
                            <span className="text-destructive text-center text-sm">{error}</span>
                        )}
                        </div>
                        <div className="flex gap-2">
                            <Button type="submit" variant="primary">
                            {editando ? 'Editar Agente' : 'Crear Agente'}
                            </Button>
                            <Button type="button" variant="ghost" onClick={onClose}>
                            Cancelar
                            </Button>
                        </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    )

}